import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { Check, Sync } from '@mui/icons-material'
import { brandColors, serif } from './brand'
import {
  SyncRun,
  SyncTeam,
  SyncUser,
  ago,
  getSyncStatus,
  runSync,
  syncTeamsApi,
  usePolled,
} from './api'
import { Page } from './ui'

function since(iso: string): string {
  const a = ago(iso)
  return a === 'just now' ? a : `${a} ago`
}

function RunChip({ run }: { run?: SyncRun }): React.JSX.Element {
  if (!run) return <Chip size='small' variant='outlined' label='Never synced' />
  if (!run.finishedAt) return <Chip size='small' label='Syncing…' />
  return (
    <Chip
      size='small'
      color={run.ok ? 'success' : 'error'}
      variant='outlined'
      label={`${run.ok ? 'Synced' : 'Sync failed'} ${since(run.finishedAt)}`}
    />
  )
}

function LogDialog(props: {
  run?: SyncRun
  onClose: () => void
}): React.JSX.Element {
  const c = brandColors[useTheme().palette.mode]
  return (
    <Dialog
      open={Boolean(props.run)}
      onClose={props.onClose}
      maxWidth='md'
      fullWidth
    >
      <DialogTitle className='erecords-serif' sx={{ fontFamily: serif }}>
        Entra sync #{props.run?.id} ({props.run?.trigger})
      </DialogTitle>
      <DialogContent>
        <Box
          component='pre'
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: '10px',
            bgcolor: c.bg,
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            maxHeight: '60vh',
            overflow: 'auto',
          }}
        >
          {props.run?.log || '(no output)'}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

// EntraSyncPanel shows the Microsoft Entra sync status with a "Sync now"
// button (admins). compact renders a one-line header widget; otherwise a card
// with the recent runs (Admin > Entra sync).
export default function EntraSyncPanel(props: {
  compact?: boolean
}): React.JSX.Element {
  const c = brandColors[useTheme().palette.mode]
  const status = usePolled(getSyncStatus, 60_000)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [shown, setShown] = useState<SyncRun>()

  const sync = (): void => {
    setBusy(true)
    setErr('')
    runSync()
      .then((run) => {
        status.refresh()
        if (!run.ok) setShown(run)
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setBusy(false))
  }

  const last = status.data?.runs[0]
  const button = (
    <Button
      size='small'
      variant='outlined'
      startIcon={busy ? <CircularProgress size={14} /> : <Sync />}
      disabled={busy || !status.data?.configured}
      onClick={sync}
    >
      {busy ? 'Syncing…' : 'Sync now'}
    </Button>
  )

  if (props.compact) {
    return (
      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
        sx={{ flexWrap: 'wrap' }}
      >
        <Typography sx={{ fontSize: 13, color: c.muted }}>
          Microsoft Entra
        </Typography>
        <Box
          sx={{ cursor: last ? 'pointer' : undefined }}
          onClick={() => setShown(last)}
        >
          <RunChip run={last} />
        </Box>
        {button}
        {err && (
          <Typography sx={{ fontSize: 12, color: 'error.main' }}>
            {err}
          </Typography>
        )}
        <LogDialog run={shown} onClose={() => setShown(undefined)} />
      </Stack>
    )
  }

  return (
    <Card
      elevation={0}
      sx={{ borderRadius: '16px', border: `1px solid ${c.line}`, p: 2.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography
          component='h2'
          className='erecords-serif'
          sx={{ fontFamily: serif, fontSize: 22, flex: 1 }}
        >
          Sync status
        </Typography>
        {button}
      </Box>
      <Typography sx={{ fontSize: 14, color: c.muted, mb: 2 }}>
        Creates On-Call users for the teams above before their first sign-in,
        merges the duplicate the first Microsoft sign-in creates, removes people
        who left, adds their Entra phone for SMS and calls (and Teams when the
        bot is set up), copies their photo and time zone, and keeps the admin
        role in step with “eRecords Admins”. Runs every{' '}
        {status.data?.interval ?? '1h'}.
      </Typography>
      {status.data && !status.data.configured && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Not configured: set ERECORDS_ENTRA_TENANT_ID,
          ERECORDS_ENTRA_CLIENT_ID, ERECORDS_ENTRA_CLIENT_SECRET and
          ERECORDS_ENTRA_ADMINS_GROUP_ID on the GoAlert deployment.
        </Alert>
      )}
      {(err || status.error) && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {err || status.error?.message}
        </Alert>
      )}
      <Stack spacing={1}>
        {status.data?.runs.map((r) => (
          <Box
            key={r.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <RunChip run={r} />
            <Typography sx={{ fontSize: 13, color: c.muted, flex: 1 }}>
              #{r.id} · {r.trigger} · {new Date(r.startedAt).toLocaleString()}
            </Typography>
            <Button size='small' onClick={() => setShown(r)} disabled={!r.log}>
              Log
            </Button>
          </Box>
        ))}
      </Stack>
      <LogDialog run={shown} onClose={() => setShown(undefined)} />
    </Card>
  )
}

type Preview = Awaited<ReturnType<typeof syncTeamsApi.preview>>

function PeopleList(props: {
  title: string
  people: SyncUser[]
  tone?: string
}): React.JSX.Element | null {
  const c = brandColors[useTheme().palette.mode]
  if (!props.people.length) return null
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography sx={{ fontWeight: 600, color: props.tone ?? c.ink }}>
        {props.title}
      </Typography>
      {props.people.map((p) => (
        <Typography key={p.email} sx={{ fontSize: 14, color: c.ink }}>
          {p.name}{' '}
          <Box component='span' sx={{ color: c.muted }}>
            {p.email}
          </Box>
        </Typography>
      ))}
    </Box>
  )
}

// SyncTeams picks the Microsoft Teams teams whose members are synced; the
// change is previewed (who is added, who is removed) before it is saved.
function SyncTeams(props: { onSaved: () => void }): React.JSX.Element {
  const theme = useTheme()
  const c = brandColors[theme.palette.mode]
  const [data, setData] = useState<{ teams: SyncTeam[]; selected: string[] }>()
  const [picked, setPicked] = useState<string[]>([])
  const [err, setErr] = useState('')
  const [preview, setPreview] = useState<Preview>()
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    syncTeamsApi
      .get()
      .then((d) => {
        setData(d)
        setPicked(d.selected)
      })
      .catch((e: Error) => setErr(e.message))
  }, [])

  const changed =
    !!data &&
    (picked.length !== data.selected.length ||
      picked.some((id) => !data.selected.includes(id)))

  const review = (): void => {
    setBusy(true)
    setErr('')
    syncTeamsApi
      .preview(picked)
      .then(setPreview)
      .catch((e: Error) => setErr(e.message))
      .finally(() => setBusy(false))
  }
  const save = (): void => {
    setBusy(true)
    syncTeamsApi
      .set(picked)
      .then(() => {
        setData(data && { ...data, selected: picked })
        setPreview(undefined)
        props.onSaved()
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setBusy(false))
  }

  return (
    <Card
      elevation={0}
      sx={{ borderRadius: '16px', border: `1px solid ${c.line}`, p: 2.5 }}
    >
      <Typography
        component='h2'
        className='erecords-serif'
        sx={{ fontFamily: serif, fontSize: 22, mb: 0.5 }}
      >
        Teams to sync
      </Typography>
      <Typography sx={{ fontSize: 14, color: c.muted, mb: 2 }}>
        Members of these Microsoft Teams teams get On-Call users. People who
        signed in with Microsoft but are in none of them are removed. Members of
        “eRecords Admins” are admins; everyone else is a user.
      </Typography>
      {err && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}
      {!data && !err && (
        <Typography sx={{ color: c.muted }}>Loading teams…</Typography>
      )}
      {data && (
        <React.Fragment>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {data.teams.map((t) => {
              const on = picked.includes(t.id)
              return (
                <Chip
                  key={t.id}
                  label={t.name}
                  icon={on ? <Check /> : undefined}
                  color={on ? 'primary' : 'default'}
                  variant={on ? 'filled' : 'outlined'}
                  onClick={() =>
                    setPicked(
                      on ? picked.filter((x) => x !== t.id) : [...picked, t.id],
                    )
                  }
                />
              )
            })}
          </Box>
          {picked.length === 0 && (
            <Typography sx={{ fontSize: 13, color: c.muted, mt: 1.5 }}>
              Nothing selected: only the “eRecords Admins” group is synced.
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant='contained'
              disabled={!changed || busy}
              onClick={review}
            >
              {busy && !preview ? 'Checking…' : 'Review and save'}
            </Button>
            {changed && (
              <Button onClick={() => setPicked(data.selected)}>Undo</Button>
            )}
          </Box>
        </React.Fragment>
      )}
      <Dialog
        open={!!preview}
        onClose={() => !busy && setPreview(undefined)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle className='erecords-serif' sx={{ fontFamily: serif }}>
          Sync{' '}
          {picked.length
            ? data?.teams
                .filter((t) => picked.includes(t.id))
                .map((t) => t.name)
                .join(', ')
            : 'eRecords Admins only'}
          ?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: c.muted }}>
            {preview?.members} people in the selected teams. The change applies
            right away with a sync.
          </Typography>
          <PeopleList title='Will be added' people={preview?.add ?? []} />
          <PeopleList
            title='Will be removed from On-Call'
            people={preview?.remove ?? []}
            tone={theme.palette.error.main}
          />
          {preview && !preview.add.length && !preview.remove.length && (
            <Typography sx={{ mt: 1.5 }}>
              Nobody is added or removed.
            </Typography>
          )}
          {!!preview?.remove.length && (
            <Alert severity='warning' sx={{ mt: 2 }}>
              Removed people lose their On-Call user, contact methods and
              schedule spots. They come back if they join a synced team.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(undefined)} disabled={busy}>
            Back
          </Button>
          <Button
            variant='contained'
            color={preview?.remove.length ? 'error' : 'primary'}
            onClick={save}
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save and sync'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

// EntraSyncPage is Admin > Microsoft Entra sync.
export function EntraSyncPage(): React.JSX.Element {
  const [key, setKey] = useState(0)
  return (
    <Page title='Microsoft Entra sync' maxWidth={1000}>
      <Stack spacing={2}>
        <SyncTeams
          onSaved={() => {
            runSync()
              .catch(() => {})
              .finally(() => setKey(key + 1))
          }}
        />
        <EntraSyncPanel key={key} />
      </Stack>
    </Page>
  )
}
